// biome-ignore lint/style/noRestrictedImports: component usage
import { Root as RadixTooltip, Provider as TooltipProvider } from "@radix-ui/react-tooltip";
import React from "react";

import { TooltipContextProvider, useTooltipConfig } from "./_context";
import { enable, useIsEnabled } from "./_enabler";

export interface TooltipProps extends React.ComponentProps<typeof RadixTooltip> {
	/**
	 * Hides the tooltip content when set to `true`.
	 * When set to `true`, only the trigger element is rendered, and the tooltip content is not shown.
	 *
	 * @default false
	 */
	hide?: boolean;

	/**
	 * When `true`, the tooltip component is loaded on hover/focus of the trigger,
	 * otherwise it is loaded when the component is mounted.
	 *
	 * @default true
	 */
	lazy?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({ hide = false, lazy = true, children, ...props }) => {
	const isEnabled = useIsEnabled();
	const config = useTooltipConfig();

	if (hide) {
		return <HiddenTooltip>{children}</HiddenTooltip>;
	}

	if (!isEnabled) {
		return <DisabledTooltip open={!lazy || (props.open ?? props.defaultOpen ?? false)}>{children}</DisabledTooltip>;
	}

	return (
		<React.Suspense fallback={<HiddenTooltip>{children}</HiddenTooltip>}>
			<TooltipProvider {...config}>
				<RadixTooltip {...props}>{children}</RadixTooltip>
			</TooltipProvider>
		</React.Suspense>
	);
};

const DisabledTooltip: React.FC<{
	open: boolean;
	children: React.ReactNode;
}> = ({ open, children }) => {
	React.useLayoutEffect(() => {
		// enable when programmatically opened
		if (open) {
			enable();
		}
	}, [open]);

	return <>{children}</>;
};

const HiddenTooltip: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => <TooltipContextProvider value={true}>{children}</TooltipContextProvider>;
